import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { NotificationActionPayload } from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/context/ToastContext';
import {
  parseActionPayload,
  resolveRpcCallable,
} from './notificationActionDispatch';

interface Props {
  actions: NotificationActionPayload[];
  className?: string;
}

export function NotificationActionButtons({ actions, className }: Props) {
  const gt = useGT();
  const { showToast } = useToast();
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);

  if (actions.length === 0) return null;

  const dispatch = async (action: NotificationActionPayload, index: number) => {
    const callable = resolveRpcCallable(action.endpoint);
    if (!callable) {
      showToast({
        title: gt('Action unavailable'),
        message: gt('No handler registered for this action.'),
        color: 'error',
      });
      return;
    }
    const parsed = parseActionPayload(action.actionPayload);
    if (!parsed.ok) {
      showToast({
        title: gt('Action failed'),
        message: gt('Invalid action payload.'),
        color: 'error',
      });
      return;
    }
    setPendingIndex(index);
    try {
      await callable(parsed.value);
      showToast({
        title: action.name,
        message: gt('Action completed.'),
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: gt('Action failed'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    } finally {
      setPendingIndex(null);
    }
  };

  return (
    <div className={className ?? 'mt-2.5 flex flex-wrap gap-2'}>
      {actions.map((action, index) => (
        <Button
          key={`${action.endpoint}:${action.name}:${index}`}
          variant={action.buttonType === 'primary' ? 'default' : 'outline'}
          size="sm"
          disabled={pendingIndex !== null}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void dispatch(action, index);
          }}
        >
          {pendingIndex === index ? <T>Working…</T> : action.name}
        </Button>
      ))}
    </div>
  );
}
